/**
 * FundraiserGallery.tsx - Public Fundraiser Gallery Display
 *
 * This component renders a public gallery view of fundraisers with filtering,
 * search, and responsive grid layout. Used for public-facing fundraiser discovery.
 *
 * Key Features:
 * - Responsive grid layout for fundraiser cards
 * - Search and filtering capabilities
 * - Gallery settings integration (title, iconMedia, colors)
 * - Fundraiser selection and filtering by status/timeframe
 * - Mobile-responsive design with touch interactions
 * - Back navigation to dashboard
 *
 * Gallery Settings:
 * - Custom title and branding
 * - Logo display with color theming
 * - Background color customization
 * - Fundraiser display filtering (all, active, specific)
 *
 * Props: None (uses MobX store for gallery settings and fundraisers)
 *
 * Dependencies: MobX store, FundraiserCard component, gallery settings
 */

import React, { useState } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { ArrowLeft, Search, X, LayoutGrid } from "lucide-react";
import * as Icons from "lucide-react";
import { fundraiserStore } from "../stores/FundraiserStore";
import { FundraiserCard } from "./FundraiserCard";
import { EditActionsDropdown } from "./EditActionsDropdown";
import { UpdateGalleryLinkModal } from "./UpdateGalleryLinkModal";
import { SocialShareModal } from "./SocialShareModal";
import { EmbedGalleryModal } from "./EmbedGalleryModal";
import { Toast } from "./Toast";
import { isLightColor } from "../utils/colorUtils";
import { Fundraiser } from "../models/FundraisersGridModels";
import {
  AuthService,
  Loader,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { firstValueFrom } from "rxjs";
import { FundraiserService } from "../services";
import { GallerySettingsModel } from "../models/GalleryModels";
import { FundRaiserStatus, IconType } from "../enums";
import { CreateFundraiserModel } from "../models";
import { NavigateFunction, useParams } from "react-router-dom";
import ToastService from "./ToastService";
import { sanitizeHtml } from "@mindmemobile/mm-payment-modules";
const GalleryContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(
    135deg,
    #f8fafc 0%,
    #e2e8f0 25%,
    #f1f5f9 50%,
    #ffffff 100%
  );
  padding: 32px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  margin-bottom: 64px;
  margin-top: 32px;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
`;

const HeaderLeft = styled.div`
  position: absolute;
  left: 0;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const HeaderRight = styled.div`
  position: absolute;
  right: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderCenter = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  &:hover {
    background: rgba(255, 255, 255, 1);
    transform: translateY(-1px);
  }
`;

const Title = styled.h1<{ color?: string; isEmbedPreview?: boolean }>`
  font-size: 32px;
  font-weight: 800;
  color: ${(props) => props.color || "#1e293b"};
  margin: 0;
  ${(props) =>
    props.isEmbedPreview &&
    `
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `}
`;

const LogoContainer = styled.div<{ isEmbedPreview?: boolean }>`
  display: flex;
  align-items: center;
  gap: 16px;
  max-width: ${(props) => (props.isEmbedPreview ? "100%" : "800px")};
  width: 100%;
  overflow: hidden;
`;

const GalleryLogo = styled.img`
  height: 48px;
  width: auto;
  border-radius: 8px;
  flex-shrink: 0;
`;

const InlineGallerySvg = styled.div<{ logoColor?: string }>`
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    height: 100%;
    width: auto;
    max-height: 48px;
    border-radius: 8px;

    * {
      fill: ${(props) => props.logoColor || "#1e293b"} !important;
    }

    path,
    circle,
    rect,
    polygon,
    ellipse,
    polyline,
    line {
      fill: ${(props) => props.logoColor || "#1e293b"} !important;
    }
  }
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SearchContainer = styled.div`
  margin-bottom: 47px;
  display: flex;
  justify-content: center;
`;

const SearchWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 500px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 16px 20px 16px 34px;
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  font-size: 18px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  transition: all 0.2s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);

  &:focus {
    outline: none;
    border-color: #10b981;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  width: 22px;
  height: 22px;
`;

const ClearButton = styled.button`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    color: #1e293b;
    background: #f1f5f9;
  }
`;

const FundraisersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
  color: #64748b;
`;

const EmptyStateIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  color: #94a3b8;
`;

const EmptyStateTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 12px 0;
`;

const EmptyStateText = styled.p`
  font-size: 16px;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
  max-width: 400px;
`;

interface FundraiserGalleryProps {
  isEmbedPreview?: boolean;
  isPublicView?: boolean;
  promoterId: string;
  navigate: NavigateFunction;
  shortUniqueId?: string;
}

const FundraiserGallery: React.FC<FundraiserGalleryProps> = observer(
  ({
    isEmbedPreview = false,
    isPublicView = false,
    promoterId,
    navigate,
    shortUniqueId,
  }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [showSocialShareModal, setShowSocialShareModal] = useState(false);
    const [showEmbedModal, setShowEmbedModal] = useState(false);
    const [showLoader, setShowLoader] = useState(true);

    const params = useParams();
    // const split = ((params && params["*"]) || "").split("/");
    // let galleryId: string | null = split[split.length - 1] || null;
    // const galleryName = UtilityMethods.isValidObjectId(galleryId || "")
    //   ? ""
    //   : galleryId;
    let galleryId = params["galleryID"];
    galleryId = UtilityMethods.isValidObjectId(galleryId || "")
      ? galleryId
      : undefined;

    const galleryName = params["galleryName"] || null;
    const shortUniqueIdParam = params["shortUniqueId"] || shortUniqueId;
    React.useEffect(() => {
      isPublicView ? loadPublicGallery() : loadGalleries();
    }, []);

    const loadPublicGallery = async () => {
      try {
        const response = galleryId
          ? await firstValueFrom(
              FundraiserService.getGallerySettings(galleryId)
            )
          : galleryName
            ? await firstValueFrom(
                FundraiserService.getPublicGallery(
                  shortUniqueIdParam || "",
                  galleryName || ""
                )
              )
            : null;
        if (response?.result) {
          const data = GallerySettingsModel.deserializeAPIModel(
            response.result
          );
          fundraiserStore.setGallerySettings(data);
          await fetchFundraisers(data.id, data.accountId);
        }
      } catch (error) {
        console.error("Error loading gallery settings:", error);
      } finally {
        setShowLoader(false);
      }
    };

    const loadGalleries = async () => {
      try {
        const response = await firstValueFrom(
          FundraiserService.getGalleries(AuthService.authData.accountId)
        );
        if (response.result) {
          const galleries = response.result.map((g) =>
            GallerySettingsModel.deserializeAPIModel(g)
          );
          fundraiserStore.setGallerySettings(
            galleries[0] || new GallerySettingsModel()
          );

          if (galleries?.length > 0) {
            await fetchFundraisers(galleries[0].id, galleries[0].accountId);
          } else {
            await fetchFundraisers("", AuthService.authData.accountId);
          }
        }
      } catch (error) {
        console.error("Error loading gallery settings:", error);
      } finally {
        setShowLoader(false);
      }
    };

    const fetchFundraisers = async (galleryId: string, accountId: string) => {
      try {
        const response = await firstValueFrom(
          FundraiserService.getFundraisers(
            accountId,
            1,
            10000,
            searchTerm,
            undefined,
            undefined,
            undefined,
            undefined,
            galleryId,
            undefined,
            promoterId
          )
        );
        const data = response.result.map((item) =>
          CreateFundraiserModel.deserializeGridAPIModel(item)
        );
        fundraiserStore.setGalleryFundRaisers(data);
      } catch (error) {
        console.error("Failed to load fundraisers", error);
      }
    };

    // Auto-scroll to top when Gallery opens
    React.useEffect(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

    // // Check for gallery settings save success toast
    // React.useEffect(() => {
    //   // Check if we're coming from gallery settings and should show success toast
    //   const showSuccessToast = sessionStorage.getItem("gallerySettingsSaved");

    //   if (showSuccessToast === "true") {
    //     setToastMessage("Gallery settings saved successfully");
    //     setShowToast(true);
    //     sessionStorage.removeItem("gallerySettingsSaved");
    //   }
    // }, []);

    const { gallerySettings } = fundraiserStore;

    // Helper function to render icons
    const renderIcon = (iconName: string) => {
      const IconComponent = (Icons as any)[iconName];
      return IconComponent ? <IconComponent size={38} /> : null; // Increased from 24 to 38 (60% larger)
    };

    //const displayedFundraisers = fundraiserStore.getFilteredGalleryFundraisers;

    const filteredFundraisers = fundraiserStore.galleryFundRaisers
      .filter((fundraiser) => {
        if (!searchTerm) return true;

        const searchLower = searchTerm.toLowerCase();
        return (
          fundraiser.title.toLowerCase().includes(searchLower) ||
          fundraiser.name.toLowerCase().includes(searchLower) ||
          (fundraiser.subtitle &&
            fundraiser.subtitle.toLowerCase().includes(searchLower)) ||
          (fundraiser.category &&
            fundraiser.category?.name?.toLowerCase().includes(searchLower)) ||
          fundraiser.description.toLowerCase().includes(searchLower)
        );
      })
      .map((f) => {
        return new CreateFundraiserModel(f);
      });

    const handleFundraiserClick = (fundraiser: CreateFundraiserModel) => {
      fundraiserStore.setSelectedFundraiser(fundraiser.id);
      fundraiserStore.setSelectedPromoter(promoterId || "");
      const fundraiserUrl = fundraiserStore.getFundraiserPublicLinkFromModel(
        fundraiser,
        promoterId || ""
      );
      window.open(fundraiserUrl, "_blank");
    };

    const clearSearch = () => {
      setSearchTerm("");
    };

    const handleEdit = () => {
      // fundraiserStore.setView("gallery-settings");
      navigate("/fundraiser/gallery-settings");
    };

    const handleUpdateLink = () => {
      setShowLinkModal(true);
    };

    const handleShare = () => {
      setShowSocialShareModal(true);
    };

    const handleEmbed = () => {
      setShowEmbedModal(true);
    };

    const handleLinkCopied = (message: string) => {
      ToastService.showSuccess(message);
    };

    // Function to extract and render SVG content with color for gallery display
    const renderGallerySvgWithColor = (svgDataUrl: string, color: string) => {
      try {
        // Only process base64 encoded SVG data URLs or inline SVG strings
        if (
          !svgDataUrl.toLowerCase().includes("data:image/svg") &&
          !svgDataUrl.startsWith("<svg")
        ) {
          return null;
        }

        // Handle inline SVG strings
        if (svgDataUrl.startsWith("<svg")) {
          const parser = new DOMParser();
          const svgDoc = parser.parseFromString(svgDataUrl, "image/svg+xml");
          const svgElement = svgDoc.querySelector("svg");

          if (svgElement) {
            const allElements = svgElement.querySelectorAll("*");
            allElements.forEach((el) => {
              if (
                el.hasAttribute("fill") &&
                el.getAttribute("fill") !== "none"
              ) {
                el.setAttribute("fill", color);
              }
            });

            if (!svgElement.hasAttribute("fill")) {
              svgElement.setAttribute("fill", color);
            }

            return sanitizeHtml(svgElement.outerHTML);
          }
          return null;
        }

        // Extract the SVG content from the data URL
        const base64Content = svgDataUrl.split(",")[1];
        if (!base64Content) {
          return null;
        }
        const svgContent = atob(base64Content);

        // Parse and modify the SVG
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svgContent, "image/svg+xml");
        const svgElement = svgDoc.querySelector("svg");

        if (svgElement) {
          // Remove any existing fill attributes and add the new color
          const allElements = svgElement.querySelectorAll("*");
          allElements.forEach((el) => {
            if (el.hasAttribute("fill") && el.getAttribute("fill") !== "none") {
              el.setAttribute("fill", color);
            }
          });

          // Set fill on the root SVG if it doesn't have one
          if (!svgElement.hasAttribute("fill")) {
            svgElement.setAttribute("fill", color);
          }

          return sanitizeHtml(svgElement.outerHTML);
        }
      } catch (error) {
        console.error("Error processing gallery SVG:", error);
      }
      return null;
    };

    // Check if iconMedia is SVG
    const isLogoSvg = (imageUrl: string): boolean => {
      if (!imageUrl) return false;
      return (
        imageUrl.toLowerCase().includes("data:image/svg") ||
        imageUrl.startsWith("<svg")
      );
    };

    if (showLoader) {
      return <>{new Loader(true).loaderComponent}</>;
    }

    return (
      <GalleryContainer
        style={{
          background:
            gallerySettings.backgroundColor.rgba ||
            gallerySettings.backgroundColor.hex,
        }}
      >
        <Header>
          <HeaderLeft className="gallery-header-left">
            {!isPublicView && (
              <BackButton onClick={() => navigate("/fundraiser/dashboard")}>
                <ArrowLeft size={20} />
              </BackButton>
            )}
          </HeaderLeft>
          <HeaderCenter>
            <LogoContainer isEmbedPreview={isEmbedPreview}>
              {gallerySettings.iconType === IconType.Image &&
                gallerySettings.iconMedia &&
                gallerySettings.iconColor && (
                  <>
                    {isLogoSvg(gallerySettings.iconMedia.fullURL) ? (
                      <InlineGallerySvg
                        logoColor={
                          gallerySettings.iconColor.rgba ||
                          gallerySettings.iconColor.hex
                        }
                        dangerouslySetInnerHTML={{
                          __html:
                            renderGallerySvgWithColor(
                              gallerySettings.iconMedia.fullURL,
                              gallerySettings.iconColor.rgba ||
                                gallerySettings.iconColor.hex ||
                                "#1e293b"
                            ) || "",
                        }}
                      />
                    ) : (
                      <GalleryLogo
                        src={gallerySettings.iconMedia.fullURL}
                        alt=""
                      />
                    )}
                  </>
                )}
              {gallerySettings.iconType === IconType.Icon &&
                gallerySettings.icon && (
                  <div
                    style={{
                      color:
                        gallerySettings.iconColor?.rgba ||
                        gallerySettings.iconColor?.hex ||
                        "#10b981",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: "4px",
                      height: "48px",
                    }}
                  >
                    {renderIcon(gallerySettings.icon)}
                  </div>
                )}
              <Title
                color={
                  gallerySettings.titleColor.rgba ||
                  gallerySettings.titleColor.hex
                }
                isEmbedPreview={isEmbedPreview}
                style={{
                  whiteSpace: isEmbedPreview ? "nowrap" : "normal",
                  overflow: isEmbedPreview ? "hidden" : "visible",
                  textOverflow: isEmbedPreview ? "ellipsis" : "clip",
                }}
              >
                {gallerySettings.title || "Fundraiser Gallery"}
              </Title>
            </LogoContainer>
          </HeaderCenter>

          <HeaderRight className="gallery-header-right">
            {!isPublicView && (
              <EditActionsDropdown
                hideWhenNoId={!gallerySettings?.id}
                onEdit={handleEdit}
                onUpdateLink={handleUpdateLink}
                onShare={handleShare}
                onEmbed={handleEmbed}
              />
            )}
          </HeaderRight>
        </Header>

        <ContentContainer>
          {(filteredFundraisers.length > 0 || searchTerm) && (
            <SearchContainer>
              <SearchWrapper>
                <SearchIcon />
                <SearchInput
                  type="text"
                  placeholder="Search fundraisers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <ClearButton onClick={clearSearch} title="Clear search">
                    <X size={18} />
                  </ClearButton>
                )}
              </SearchWrapper>
            </SearchContainer>
          )}

          {filteredFundraisers.length === 0 ? (
            <EmptyState
              style={{
                marginTop: "50px",
                padding: "80px 60px",
                border: "2px solid #e2e8f0",
                borderRadius: "16px",
                background: "#ffffff",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                maxWidth: "700px",
                minHeight: "300px",
                margin: "100px auto 0",
              }}
            >
              <EmptyStateIcon>
                <LayoutGrid size={40} />
              </EmptyStateIcon>
              <EmptyStateTitle>
                {searchTerm ? "No Fundraisers Found" : "No Active Fundraisers"}
              </EmptyStateTitle>
              <EmptyStateText style={{ marginTop: "20px" }}>
                {searchTerm
                  ? `No active fundraisers match "${searchTerm}".`
                  : "There are currently no active fundraisers available for donations."}
              </EmptyStateText>
            </EmptyState>
          ) : (
            <FundraisersGrid
              style={{
                gridTemplateColumns: isEmbedPreview
                  ? "repeat(2, 1fr)"
                  : undefined,
                gap: isEmbedPreview ? "24px" : undefined,
              }}
            >
              {filteredFundraisers.map((fundraiser) => (
                <FundraiserCard
                  key={fundraiser.id}
                  fundraiser={fundraiser}
                  onClick={() => handleFundraiserClick(fundraiser)}
                />
              ))}
            </FundraisersGrid>
          )}
        </ContentContainer>

        <UpdateGalleryLinkModal
          isVisible={showLinkModal}
          gallerySettings={gallerySettings}
          onClose={() => setShowLinkModal(false)}
          onLinkCopied={handleLinkCopied}
        />

        <SocialShareModal
          gallerySettings={gallerySettings}
          isVisible={showSocialShareModal}
          onClose={() => setShowSocialShareModal(false)}
          shortUniqueId={shortUniqueIdParam || ""}
        />

        <EmbedGalleryModal
          gallerySettings={gallerySettings}
          isVisible={showEmbedModal}
          onClose={() => setShowEmbedModal(false)}
        />
      </GalleryContainer>
    );
  }
);

export default withRouter(FundraiserGallery);
